
import React from "react";
import { Helmet } from "react-helmet-async";
import Layout from "../components/Layout";

const PrivacyPolicy = () => (
  <Layout>
    <Helmet>
      <title>Privacy Policy - Data Protection & User Privacy | TrueCore Gadgets India</title>
      <meta name="description" content="TrueCore Gadgets privacy policy - How we collect, use, and protect your personal information. Data security, user privacy, and compliance with Indian privacy laws." />
      <meta name="keywords" content="TrueCore privacy policy, data protection, user privacy, personal information, data security, privacy compliance, Indian privacy laws" />
      
      {/* Canonical URL */}
      <link rel="canonical" href="https://truecoregadgets.com/privacy-policy" />
      
      {/* Open Graph Tags */}
      <meta property="og:type" content="website" />
      <meta property="og:title" content="Privacy Policy - Data Protection & User Privacy | TrueCore Gadgets" />
      <meta property="og:description" content="Learn how TrueCore Gadgets protects your personal information and ensures data privacy compliance." />
      <meta property="og:url" content="https://truecoregadgets.com/privacy-policy" />
      <meta property="og:image" content="/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
      
      {/* Twitter Card Tags */}
      <meta name="twitter:card" content="summary" />
      <meta name="twitter:title" content="Privacy Policy - TrueCore Gadgets" />
      <meta name="twitter:description" content="Data protection and user privacy policy for TrueCore Gadgets services." />
      <meta name="twitter:image" content="/lovable-uploads/461a823a-5781-4980-ad39-ffec726a88a3.png" />
    </Helmet>
    
    <div className="container mx-auto py-12 px-4 max-w-4xl">
      <h1 className="text-4xl font-bold mb-8 text-primary text-center">Privacy Policy</h1>
      
      <div className="bg-muted/50 rounded-lg p-6 mb-8">
        <p className="text-lg">
          At TrueCore Gadgets (operated by Truecore Innovations), we are committed to protecting your privacy and ensuring the security of your personal information. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you visit our website or use our services.
        </p>
      </div>

      <div className="space-y-8">
        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">1. Information We Collect</h2>
          
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-semibold mb-3 text-foreground">Personal Information</h3>
              <p className="text-muted-foreground mb-3">We may collect the following personal information when you:</p>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li><strong>Create an account:</strong> Name, email address, phone number, password</li>
                <li><strong>Place an order:</strong> Billing address, shipping address, payment information</li>
                <li><strong>Contact us:</strong> Name, email, phone number, message content, company details</li>
                <li><strong>Subscribe to newsletters:</strong> Email address, communication preferences</li>
                <li><strong>Apply for bulk orders:</strong> Business information, tax details, authorized representative details</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3 text-foreground">Technical Information</h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>IP address and geographic location</li>
                <li>Browser type, version, and operating system</li>
                <li>Device information and screen resolution</li>
                <li>Website usage patterns and click-stream data</li>
                <li>Cookies and similar tracking technologies</li>
                <li>Referral source and exit pages</li>
              </ul>
            </div>

            <div>
              <h3 className="text-lg font-semibold mb-3 text-foreground">Business Information</h3>
              <ul className="list-disc pl-6 space-y-2 text-muted-foreground">
                <li>Company name, registration details, and tax information</li>
                <li>Business requirements and custom specifications</li>
                <li>Purchase history and order preferences</li>
                <li>Communication history and support interactions</li>
              </ul>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">2. How We Use Your Information</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>We use the collected information for the following purposes:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Order Processing</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Process and fulfill orders</li>
                  <li>Payment processing and verification</li>
                  <li>Shipping and delivery coordination</li>
                  <li>Order status updates and notifications</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Customer Service</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Respond to inquiries and support requests</li>
                  <li>Provide technical assistance</li>
                  <li>Handle returns and warranty claims</li>
                  <li>Resolve disputes and complaints</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Marketing & Communication</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Send promotional emails and newsletters</li>
                  <li>Announce new products and services</li>
                  <li>Provide personalized recommendations</li>
                  <li>Conduct market research and surveys</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Legal & Compliance</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Comply with legal obligations</li>
                  <li>Prevent fraud and security threats</li>
                  <li>Enforce terms and conditions</li>
                  <li>Maintain business records</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">3. Information Sharing and Disclosure</h2>
          <div className="space-y-4 text-muted-foreground">
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <p className="font-semibold text-green-800 mb-2">🛡️ We Never Sell Your Data</p>
              <p className="text-green-700">We do not sell, rent, or lease your personal information to third parties for marketing purposes.</p>
            </div>
            
            <p>We may share your information in the following limited circumstances:</p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-foreground">Service Providers</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Payment processors (for secure transaction processing)</li>
                  <li>Shipping companies (for order delivery)</li>
                  <li>Cloud hosting services (for data storage and website operation)</li>
                  <li>Email service providers (for communications)</li>
                  <li>Customer support tools (for service delivery)</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2 text-foreground">Legal Requirements</h3>
                <ul className="list-disc pl-6 space-y-1">
                  <li>Compliance with court orders or legal process</li>
                  <li>Protection of our rights and property</li>
                  <li>Prevention of fraud or illegal activities</li>
                  <li>Response to government requests or investigations</li>
                </ul>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2 text-foreground">Business Transfers</h3>
                <p>In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of the business transaction, subject to the same privacy protections.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">4. Data Security</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>We implement comprehensive security measures to protect your personal information:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Technical Safeguards</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>SSL/TLS encryption for data transmission</li>
                  <li>Encrypted data storage systems</li>
                  <li>Regular security audits and penetration testing</li>
                  <li>Secure payment processing (PCI DSS compliant)</li>
                  <li>Firewall protection and intrusion detection</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Administrative Safeguards</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Access controls and user authentication</li>
                  <li>Employee training on data protection</li>
                  <li>Regular policy reviews and updates</li>
                  <li>Incident response procedures</li>
                  <li>Data minimization practices</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">5. Cookies and Tracking Technologies</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>We use cookies and similar technologies to enhance your browsing experience:</p>
            
            <div className="space-y-4">
              <div>
                <h3 className="text-lg font-semibold mb-2 text-foreground">Types of Cookies We Use</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-medium mb-2">Essential Cookies</h4>
                    <p className="text-sm">Required for basic website functionality, shopping cart, and security features.</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-medium mb-2">Analytics Cookies</h4>
                    <p className="text-sm">Help us understand how visitors interact with our website to improve user experience.</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-medium mb-2">Marketing Cookies</h4>
                    <p className="text-sm">Used to deliver relevant advertisements and track campaign effectiveness.</p>
                  </div>
                  <div className="bg-card p-4 rounded-lg border">
                    <h4 className="font-medium mb-2">Preference Cookies</h4>
                    <p className="text-sm">Remember your settings and preferences for a personalized experience.</p>
                  </div>
                </div>
              </div>
              
              <div>
                <h3 className="text-lg font-semibold mb-2 text-foreground">Managing Cookies</h3>
                <p>You can control cookies through your browser settings. Note that disabling certain cookies may affect website functionality.</p>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">6. Your Privacy Rights</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>Under Indian privacy laws and our commitment to transparency, you have the following rights:</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Access & Portability</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Request a copy of your personal data</li>
                  <li>Download your information in a portable format</li>
                  <li>Understand how your data is being used</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Correction & Updates</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Correct inaccurate personal information</li>
                  <li>Update your account details</li>
                  <li>Modify communication preferences</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Deletion & Restriction</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Request deletion of your personal data</li>
                  <li>Restrict processing of your information</li>
                  <li>Object to certain data processing activities</li>
                </ul>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Communication Control</h3>
                <ul className="list-disc pl-4 space-y-1 text-sm">
                  <li>Opt-out of marketing communications</li>
                  <li>Unsubscribe from newsletters</li>
                  <li>Control notification preferences</li>
                </ul>
              </div>
            </div>
            
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
              <p className="text-blue-800">
                <strong>To exercise your rights:</strong> Contact us at <a href="mailto:privacy@truecoregadgets.com" className="underline">privacy@truecoregadgets.com</a> or call 022 6962 1901. We will respond to your request within 30 days.
              </p>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">7. Data Retention</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>We retain your personal information only as long as necessary for the purposes outlined in this policy:</p>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center p-3 bg-card rounded border">
                <span className="font-medium">Account Information</span>
                <span className="text-sm">Until account deletion or 3 years of inactivity</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-card rounded border">
                <span className="font-medium">Order & Transaction Data</span>
                <span className="text-sm">7 years (for tax and legal compliance)</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-card rounded border">
                <span className="font-medium">Marketing Communications</span>
                <span className="text-sm">Until unsubscribed or 2 years of inactivity</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-card rounded border">
                <span className="font-medium">Website Analytics</span>
                <span className="text-sm">26 months (Google Analytics default)</span>
              </div>
              <div className="flex justify-between items-center p-3 bg-card rounded border">
                <span className="font-medium">Support Communications</span>
                <span className="text-sm">3 years for service improvement</span>
              </div>
            </div>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">8. International Data Transfers</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              While we primarily operate within India, some of our service providers may be located outside India. When we transfer your data internationally, we ensure adequate protection through:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Standard contractual clauses approved by regulatory authorities</li>
              <li>Adequacy decisions for countries with equivalent privacy protections</li>
              <li>Certification schemes and codes of conduct</li>
              <li>Explicit consent where required by law</li>
            </ul>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">9. Children's Privacy</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              Our services are not intended for children under 18 years of age. We do not knowingly collect personal information from children under 18. If we become aware that we have collected personal information from a child under 18, we will take steps to delete such information promptly.
            </p>
            <p>
              If you are a parent or guardian and believe your child has provided us with personal information, please contact us immediately.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">10. Changes to This Privacy Policy</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              We may update this Privacy Policy from time to time to reflect changes in our practices, technology, legal requirements, or other factors. We will notify you of any material changes by:
            </p>
            <ul className="list-disc pl-6 space-y-2">
              <li>Posting the updated policy on our website</li>
              <li>Sending an email notification to registered users</li>
              <li>Displaying a prominent notice on our website</li>
              <li>Including notification in our newsletter (if subscribed)</li>
            </ul>
            <p>
              Your continued use of our services after any changes constitutes acceptance of the updated Privacy Policy.
            </p>
          </div>
        </section>

        <section>
          <h2 className="text-2xl font-bold mb-4 text-primary">11. Contact Information</h2>
          <div className="space-y-4 text-muted-foreground">
            <p>
              If you have any questions, concerns, or requests regarding this Privacy Policy or our data practices, please contact us:
            </p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">Privacy Officer</h3>
                <p className="mb-2"><strong>Email:</strong> privacy@truecoregadgets.com</p>
                <p className="mb-2"><strong>Phone:</strong> 022 6962 1901</p>
                <p><strong>Response Time:</strong> Within 30 days</p>
              </div>
              
              <div className="bg-card p-6 rounded-lg border">
                <h3 className="font-semibold mb-3 text-foreground">General Support</h3>
                <p className="mb-2"><strong>Email:</strong> support@truecoregadgets.com</p>
                <p className="mb-2"><strong>Phone:</strong> 022 6962 1901</p>
                <p><strong>Hours:</strong> Monday-Friday, 9 AM-6 PM IST</p>
              </div>
            </div>
            
            <div className="bg-card p-6 rounded-lg border">
              <h3 className="font-semibold mb-3 text-foreground">Postal Address</h3>
              <p>
                <strong>Truecore Innovations</strong><br/>
                Mumbai, Maharashtra, India<br/>
                <em>Attention: Privacy Officer</em>
              </p>
            </div>
          </div>
        </section>
      </div>

      <div className="mt-12 p-6 bg-primary/10 rounded-lg text-center">
        <p className="text-sm text-muted-foreground">
          <strong>Last updated:</strong> June 2023<br/>
          This Privacy Policy is effective as of the last updated date and supersedes all prior versions.
        </p>
      </div>
    </div>
  </Layout>
);

export default PrivacyPolicy;
